/**
 * Program IR: a typed tree built once from the Blockly workspace JSON.
 * Generators and the interpreter only ever see this tree (ADR 0007).
 * Every node keeps the id of the block it came from, for highlighting.
 */

/** What a single value is. Lists and tables hold one of these in every place. */
export type ScalarType = 'int' | 'double' | 'string' | 'bool';

/**
 * A variable's static type. A list is written `int[]` and a table `int[][]`, so the four scalars
 * give twelve types in all; `isArray`, `elemOf` and `rankOf` below keep the comparisons readable.
 *
 * `char` is one character of a text (`s[i]`), as in C++. It never fills a list: a list holds
 * whole texts, and a character put into one is a text of one character (ADR 0018).
 */
export type ValueType =
  | ScalarType
  | 'char'
  | 'int[]'
  | 'double[]'
  | 'string[]'
  | 'bool[]'
  | 'int[][]'
  | 'double[][]'
  | 'string[][]'
  | 'bool[][]';

export const SCALARS: readonly ScalarType[] = ['int', 'double', 'string', 'bool'];

export const isArray = (t: ValueType): boolean => t.endsWith(']');

/** Text or one character of it: the values letters, `find` and comparisons work on. */
export const isTextual = (t: ValueType): boolean => t === 'string' || t === 'char';

/** The type of one place in a list or table; a scalar is its own element type. */
export function elemOf(t: ValueType): ScalarType {
  return t.replace(/\[\]/g, '') as ScalarType;
}

/** 0 for a plain value, 1 for a list, 2 for a table. */
export function rankOf(t: ValueType): 0 | 1 | 2 {
  return t.endsWith('[][]') ? 2 : t.endsWith('[]') ? 1 : 0;
}

export function arrayOf(elem: ScalarType, rank: 1 | 2): ValueType {
  return rank === 1 ? (`${elem}[]` as ValueType) : (`${elem}[][]` as ValueType);
}

export type ReadKind = 'int' | 'double' | 'text';

export type ArithOp = '+' | '-' | '*' | '/' | 'idiv' | 'mod';
export type CompareOp = '==' | '!=' | '<' | '<=' | '>' | '>=';
export type LogicOp = 'and' | 'or';

export type Expr =
  | { k: 'num'; id: string; value: number }
  /** `char` marks a C++ character literal ('a'): the same text, but typed as one character. */
  | { k: 'text'; id: string; value: string; char?: true }
  | { k: 'bool'; id: string; value: boolean }
  | { k: 'var'; id: string; name: string }
  | { k: 'read'; id: string; as: ReadKind }
  | { k: 'arith'; id: string; op: ArithOp; left: Expr; right: Expr }
  | { k: 'compare'; id: string; op: CompareOp; left: Expr; right: Expr }
  | { k: 'logic'; id: string; op: LogicOp; left: Expr; right: Expr }
  | { k: 'not'; id: string; arg: Expr }
  | { k: 'join'; id: string; left: Expr; right: Expr }
  | { k: 'length'; id: string; arg: Expr }
  /** One place of a list (`a[i]`) or of a table (`a[i][j]`); on a text, one character. */
  | { k: 'at'; id: string; name: string; index: Expr; index2: Expr | null }
  /** The character's code (ASCII): `(int) c`, `ord(c)`. */
  | { k: 'code'; id: string; arg: Expr }
  /** The character with a given code: `(char) n`, `chr(n)`. */
  | { k: 'chr'; id: string; arg: Expr }
  /** A letter made capital or small; any other character stays as it is. */
  | { k: 'case'; id: string; upper: boolean; arg: Expr }
  /** Where `needle` first appears in `hay`, counting from 0; -1 when it does not. */
  | { k: 'find'; id: string; needle: Expr; hay: Expr }
  /** `len` characters of a text, starting at position `from` (from 0). */
  | { k: 'substr'; id: string; arg: Expr; from: Expr; len: Expr }
  /** How many places a list has; for a table, rows (`dim` 1) or columns (`dim` 2). */
  | { k: 'size'; id: string; name: string; dim: 1 | 2 }
  /** An empty socket. `id` is the block that owns the socket. */
  | { k: 'missing'; id: string };

export type PrintEnd = 'newline' | 'space' | 'none';

export type Stmt =
  | { k: 'set'; id: string; name: string; value: Expr }
  | { k: 'change'; id: string; name: string; delta: Expr }
  | { k: 'print'; id: string; value: Expr; end: PrintEnd }
  | { k: 'if'; id: string; branches: { cond: Expr; body: Stmt[] }[]; orElse: Stmt[] | null }
  | { k: 'repeat'; id: string; times: Expr; body: Stmt[] }
  | { k: 'while'; id: string; cond: Expr; body: Stmt[] }
  | { k: 'for'; id: string; name: string; from: Expr; to: Expr; down: boolean; body: Stmt[] }
  | { k: 'break'; id: string }
  /**
   * Makes a list or table of a fixed size, every place starting at zero (or "" / false).
   * `base` is the first index the student chose, 0 or 1.
   */
  | {
      k: 'newArray';
      id: string;
      name: string;
      elem: ScalarType;
      dims: [Expr] | [Expr, Expr];
      base: 0 | 1;
    }
  /** Puts a value in one place: `a[i] = v` or `a[i][j] = v`. */
  | { k: 'setAt'; id: string; name: string; index: Expr; index2: Expr | null; value: Expr };

export type DiagnosticCode =
  | 'no-start'
  | 'extra-start'
  | 'empty-input'
  | 'unknown-block'
  | 'read-placement'
  | 'break-outside-loop'
  | 'type-conflict'
  | 'needs-number'
  | 'needs-whole-number'
  | 'needs-text'
  | 'needs-bool'
  | 'compare-mismatch'
  /** A list or table was expected here (indexing something that is a plain value). */
  | 'needs-array'
  /** A plain whole number was expected as the index. */
  | 'needs-index'
  /** A list used as a table, or the other way round. */
  | 'wrong-rank'
  /** One character was expected (a letter of a text), not a whole text. */
  | 'needs-char';

export interface Diagnostic {
  code: DiagnosticCode;
  blockId: string | null;
  /** Values for the message template, e.g. the variable name. */
  params?: Record<string, string>;
}

export interface Program {
  /** Id of the "when program starts" block, or null when there is none. */
  startId: string | null;
  body: Stmt[];
  /** Variables in first-use order (pre-order over the tree). */
  variables: string[];
  /** Static type of every variable, from the inference pass. */
  types: Record<string, ValueType>;
  diagnostics: Diagnostic[];
}

/** Pre-order walk over statements, including nested bodies. */
export function* walkStmts(body: readonly Stmt[]): Generator<Stmt> {
  for (const s of body) {
    yield s;
    switch (s.k) {
      case 'if':
        for (const b of s.branches) yield* walkStmts(b.body);
        if (s.orElse) yield* walkStmts(s.orElse);
        break;
      case 'repeat':
      case 'while':
      case 'for':
        yield* walkStmts(s.body);
        break;
      default:
        break;
    }
  }
}

/** Direct expressions of a statement (not nested statements). */
export function stmtExprs(s: Stmt): Expr[] {
  switch (s.k) {
    case 'set':
      return [s.value];
    case 'newArray':
      return [...s.dims];
    case 'setAt':
      return s.index2 ? [s.index, s.index2, s.value] : [s.index, s.value];
    case 'change':
      return [s.delta];
    case 'print':
      return [s.value];
    case 'if':
      return s.branches.map((b) => b.cond);
    case 'repeat':
      return [s.times];
    case 'while':
      return [s.cond];
    case 'for':
      return [s.from, s.to];
    case 'break':
      return [];
  }
}

/** Pre-order walk over an expression tree. */
export function* walkExpr(e: Expr): Generator<Expr> {
  yield e;
  switch (e.k) {
    case 'arith':
    case 'compare':
    case 'logic':
    case 'join':
      yield* walkExpr(e.left);
      yield* walkExpr(e.right);
      break;
    case 'not':
    case 'length':
    case 'code':
    case 'chr':
    case 'case':
      yield* walkExpr(e.arg);
      break;
    case 'find':
      yield* walkExpr(e.needle);
      yield* walkExpr(e.hay);
      break;
    case 'substr':
      yield* walkExpr(e.arg);
      yield* walkExpr(e.from);
      yield* walkExpr(e.len);
      break;
    case 'at':
      yield* walkExpr(e.index);
      if (e.index2) yield* walkExpr(e.index2);
      break;
    default:
      break;
  }
}

/** Ids of every block inside an expression (for mapping value blocks to code lines). */
export function exprIds(e: Expr): string[] {
  const ids: string[] = [];
  for (const x of walkExpr(e)) if (x.k !== 'missing') ids.push(x.id);
  return ids;
}
